import { useEffect, useRef, useState } from "react";
import api, { fileUrl, errMsg } from "@/lib/api";
import YourStatsCard from "@/components/YourStatsCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShoppingBag, Pencil, Upload, Trash2, Plus, X } from "lucide-react";
import InviteTrialBadge from "@/components/InviteTrialBadge";

const EMPTY_SLOT = { photo: "", description: "", price: "" };

export default function MarketplacePortal() {
  const [profile, setProfile] = useState(null);
  const [slots, setSlots] = useState([]);
  const [editingIdx, setEditingIdx] = useState(null);
  const [form, setForm] = useState(EMPTY_SLOT);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const photoInput = useRef(null);

  const loadProfile = () => {
    api.get("/role-profile")
      .then((r) => {
        setProfile(r.data);
        setSlots(Array.isArray(r.data.profile?.product_slots) ? r.data.profile.product_slots : []);
      })
      .catch(() => {});
  };

  useEffect(() => { loadProfile(); }, []);

  const openEdit = (idx) => {
    const s = slots[idx] || EMPTY_SLOT;
    setEditingIdx(idx);
    setForm({ photo: s.photo || "", description: s.description || "", price: s.price || "" });
  };

  const openNew = () => {
    // Reuse the first empty slot if one exists; otherwise append a new slot
    // at the end so we don't overwrite anything the seller already saved.
    const emptyIdx = slots.findIndex((s) => !s || (!s.photo && !s.description && !s.price));
    const idx = emptyIdx === -1 ? slots.length : emptyIdx;
    setEditingIdx(idx);
    setForm({ photo: "", description: "", price: "" });
  };

  const deleteSlot = async (idx) => {
    if (!window.confirm("Delete this product listing? This cannot be undone.")) return;
    try {
      const nextSlots = slots.slice();
      nextSlots[idx] = { photo: "", description: "", price: "" };
      const p = profile?.profile || {};
      await api.put("/role-profile", {
        business_name: p.business_name || "",
        tagline: p.tagline || "",
        description: p.description || "",
        categories: p.categories || [],
        location: p.location || "",
        phone: p.phone || "",
        contact_email: p.contact_email || "",
        website: p.website || "",
        business_logo: p.business_logo || "",
        product_categories: p.product_categories || [],
        product_slots: nextSlots,
        social_link_1: p.social_link_1 || "",
        social_link_2: p.social_link_2 || "",
        social_link_3: p.social_link_3 || "",
      });
      toast.success("Listing deleted");
      loadProfile();
    } catch (e) {
      toast.error(errMsg(e, "Could not delete listing"));
    }
  };

  const closeEdit = () => { setEditingIdx(null); setForm(EMPTY_SLOT); };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Image is too large (max 10MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/marketplace-user/upload?kind=product_photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo: data.path }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const submitEdit = async () => {
    if (editingIdx === null) return;
    setSaving(true);
    try {
      const nextSlots = slots.slice();
      while (nextSlots.length <= editingIdx) nextSlots.push({ photo: "", description: "", price: "" });
      nextSlots[editingIdx] = { ...nextSlots[editingIdx], ...form };
      const p = profile?.profile || {};
      await api.put("/role-profile", {
        business_name: p.business_name || "",
        tagline: p.tagline || "",
        description: p.description || "",
        categories: p.categories || [],
        location: p.location || "",
        phone: p.phone || "",
        contact_email: p.contact_email || "",
        website: p.website || "",
        business_logo: p.business_logo || "",
        product_categories: p.product_categories || [],
        product_slots: nextSlots,
        social_link_1: p.social_link_1 || "",
        social_link_2: p.social_link_2 || "",
        social_link_3: p.social_link_3 || "",
      });
      toast.success("Listing updated");
      closeEdit();
      loadProfile();
    } catch (e) {
      toast.error(errMsg(e, "Could not save listing"));
    } finally {
      setSaving(false);
    }
  };

  const visibleSlots = slots
    .map((s, i) => ({ ...s, _idx: i }))
    .filter((s) => s && (s.photo || s.description || s.price));

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="marketplace-portal">
      <Dialog open={editingIdx !== null} onOpenChange={(o) => { if (!o) closeEdit(); }}>
        <DialogContent className="rounded-3xl bg-cream max-h-[90vh] overflow-y-auto" data-testid="mk-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">{slots[editingIdx]?.photo || slots[editingIdx]?.description || slots[editingIdx]?.price ? "Edit product listing" : "Add product"}</DialogTitle>
            <DialogDescription className="text-forest/60 text-sm">
              Update your product details. Your changes go live immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-forest/80">Product image</Label>
              <div className="mt-2 flex items-center gap-4">
                {form.photo ? (
                  <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-sage/30 shrink-0">
                    <img src={fileUrl(form.photo)} alt="Product" className="w-full h-full object-cover" data-testid="mk-edit-photo-preview" />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, photo: "" }))}
                      className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full text-terracotta-dark"
                      data-testid="mk-edit-photo-remove" aria-label="Remove photo">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-2xl bg-white/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0">
                    <Upload className="w-6 h-6" />
                  </div>
                )}
                <div>
                  <input ref={photoInput} type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="mk-edit-photo-input" />
                  <Button type="button" variant="outline" size="sm" className="rounded-full border-sage/40 text-forest"
                    onClick={() => photoInput.current?.click()} disabled={uploading} data-testid="mk-edit-photo-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? "Uploading..." : (form.photo ? "Replace image" : "Upload image")}
                  </Button>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-forest/80">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3} className="mt-1 rounded-xl bg-white border-sage/30" data-testid="mk-edit-description" />
            </div>
            <div>
              <Label className="text-forest/80">Price (Rands)</Label>
              <div className="mt-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/60 font-bold">R</span>
                <Input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="e.g. 150" className="pl-7 rounded-xl bg-white border-sage/30" data-testid="mk-edit-price" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="rounded-full" onClick={closeEdit} data-testid="mk-edit-cancel-btn">Cancel</Button>
              <Button onClick={submitEdit} disabled={saving} className="rounded-full bg-sage hover:bg-sage-dark text-white" data-testid="mk-edit-save-btn">
                {saving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-3 mb-8">
        <span className="w-11 h-11 rounded-full bg-sage flex items-center justify-center"><ShoppingBag className="w-5 h-5 text-white" /></span>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-black text-forest">Marketplace dashboard</h1>
          <p className="text-sm text-forest/60">Welcome back{profile?.profile?.business_name ? `, ${profile.profile.business_name}` : ""}</p>
          <InviteTrialBadge />
        </div>
        <Button onClick={openNew} className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white" data-testid="mk-add-product-btn">
          <Plus className="w-4 h-4 mr-1" /> Add product
        </Button>
      </div>

      <YourStatsCard />

      <section>
        <h2 className="font-heading font-bold text-forest text-lg mb-4">Your product listings</h2>
        {visibleSlots.length === 0 ? (
          <div className="bg-white/70 border border-sage/20 rounded-3xl p-8 text-center text-forest/60" data-testid="mk-empty">
            No listings yet. Tap <strong>Add product</strong> to list your first one.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {visibleSlots.map((s) => (
              <div key={s._idx} className="bg-white/80 border border-sage/20 rounded-2xl overflow-hidden shadow-sm" data-testid={`mk-slot-${s._idx}`}>
                {s.photo ? (
                  <div className="h-40 bg-sage-light flex items-center justify-center overflow-hidden">
                    <img src={fileUrl(s.photo)} alt={`Product ${s._idx + 1}`} className="w-full h-full object-contain" data-testid={`mk-slot-image-${s._idx}`} />
                  </div>
                ) : (
                  <div className="h-40 bg-sage-light flex items-center justify-center">
                    <img src="/logo.png" alt="Compass Kids logo" className="max-h-24 max-w-[70%] object-contain opacity-80" data-testid={`mk-slot-image-fallback-${s._idx}`} />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-heading font-bold text-forest">Product {s._idx + 1}</h3>
                    {s.price && (
                      <span className="shrink-0 text-sm font-black text-terracotta-dark bg-terracotta-light px-2.5 py-1 rounded-full" data-testid={`mk-slot-price-${s._idx}`}>
                        R{s.price}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-forest/70 line-clamp-3" data-testid={`mk-slot-description-${s._idx}`}>{s.description || "—"}</p>
                  <div className="mt-4 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full border-sage/40 text-forest hover:bg-sage-light hover:text-forest"
                      onClick={() => openEdit(s._idx)}
                      data-testid={`mk-edit-btn-${s._idx}`}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit listing
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light hover:text-terracotta-dark"
                      onClick={() => deleteSlot(s._idx)}
                      data-testid={`mk-delete-btn-${s._idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
